// src/pages/AboutPage.jsx  —  ohapotato v2026.10.36
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const FEATURES = [
  { icon: '🥦', title: 'Ingredient-first search',   body: 'Select what\'s in your fridge and cupboard. OhAPotato searches thousands of recipes and ranks them by how many of your ingredients they use — so food stops going to waste.' },
  { icon: '🥔', title: 'My Pantry',                 body: 'Mark the staples you always keep at home. The app remembers them across sessions so you never re-select the same ingredients twice.' },
  { icon: '🛒', title: 'Grocery List',               body: 'Any ingredient a recipe needs that you don\'t have can be added to your list in one tap. Items are consolidated so you never see duplicates.' },
  { icon: '🗓️', title: 'Weekly Meal Planner',    body: 'Generates a complete 7-day meal plan built around your pantry, then populates your Grocery List with only the items you\'re missing.' },
  { icon: '👨‍🍳', title: 'Chef Mode',              body: 'AI-powered guidance for any recipe: technique breakdowns, smart ingredient substitutions, wine and drink pairings, and plating tips.' },
  { icon: '📊', title: 'Nutrition Breakdown',    body: 'Per-serving nutrition data including calories, macros, fibre, sugar, and sodium — with daily value percentages.' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About — OhAPotato 🥔';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name = 'description'; document.head.appendChild(metaDesc); }
    metaDesc.content = 'About OhAPotato — the free ingredient-based recipe finder built by Michael Morris. Learn how it works, why it was built, and how to get in touch.';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={s.page}>
      <nav style={s.nav}>
        <Link to="/" style={s.navHome}>← Back to OhAPotato</Link>
      </nav>

      <main style={s.main}>
        <header style={s.hero}>
          <div style={s.heroEmoji}>🥔</div>
          <h1 style={s.heroTitle}>Cook what you have.<br />Not what you don't.</h1>
          <p style={s.heroSub}>
            OhAPotato is a free recipe finder that starts with your ingredients, not someone
            else's shopping list. Open your fridge, pick what's inside, and we'll show you
            every decent meal hiding in there.
          </p>
        </header>

        <section style={s.section}>
          <h2 style={s.sectionTitle}>How it works</h2>
          <ol style={s.steps}>
            <li style={s.step}><span style={s.stepNum}>1</span><div><strong>Pick your ingredients</strong> — tap the icons for what you have on hand. The more you select, the better the matches.</div></li>
            <li style={s.step}><span style={s.stepNum}>2</span><div><strong>Hit Find Recipes</strong> — our engine searches Edamam's database of over two million recipes, ranked by ingredient match percentage.</div></li>
            <li style={s.step}><span style={s.stepNum}>3</span><div><strong>Cook something good</strong> — open any recipe card for the full ingredients list, step-by-step instructions, cook time, difficulty, and nutrition info.</div></li>
          </ol>
        </section>

        <section style={s.section}>
          <h2 style={s.sectionTitle}>Features</h2>
          <div style={s.featureGrid}>
            {FEATURES.map(f => (
              <div key={f.title} style={s.featureCard}>
                <span style={s.featureIcon}>{f.icon}</span>
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureBody}>{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section style={s.section}>
          <h2 style={s.sectionTitle}>Why we built it</h2>
          <p style={s.prose}>
            The average household throws away roughly 30% of the food it buys. Most of that
            waste happens not because people are careless, but because recipes are written for
            the shop — not the kitchen. You find something you want to cook, then buy eight
            things you didn't need, use two of them, and watch the rest quietly expire.
          </p>
          <p style={s.prose}>
            OhAPotato flips that. The ingredient list comes first. The recipe comes second.
            It's a small change in direction that turns grocery shopping into a completion
            exercise rather than a fresh expedition every time you're hungry.
          </p>
          <p style={s.prose}>
            We're a small independent project. No venture capital, no growth-at-all-costs
            mandate. Just a tool that tries to be genuinely useful in the kitchen.
          </p>
        </section>

        <section style={s.section}>
          <h2 style={s.sectionTitle}>About the author</h2>
          <div style={s.authorBox}>
            <div style={s.authorAvatar}>MM</div>
            <div>
              <p style={s.authorName}>Michael Morris</p>
              <p style={s.authorHandle}>Food writer &amp; founder</p>
            </div>
          </div>
          <p style={s.prose}>
            Michael Morris is a home cook and food writer who has spent over a decade writing
            about practical, everyday cooking — how to cook well on a budget, reduce food
            waste, and build real confidence in the kitchen without fuss or unnecessary gear.
          </p>
          <p style={s.prose}>
            OhAPotato grew out of a recurring frustration: recipes are written for the shop, not
            the kitchen. You find something you want to make, then buy eight ingredients, use
            two, and watch the rest expire. Michael built OhAPotato to flip that — starting
            with what you already have and working outwards from there.
          </p>
          <p style={s.prose}>
            He writes all the cooking guides on this site. You can reach him via the{' '}
            <a href="/contact" style={{ color: 'var(--accent-orange)' }}>contact page</a>.
          </p>
        </section>



        <section style={s.section}>
          <h2 style={s.sectionTitle}>Support the project</h2>
          <p style={s.prose}>
            OhAPotato is completely free — no subscription, no account required for recipe search.
            If it saves you a trip to the shop or rescues a forgotten bag of spinach, consider
            buying us a coffee. It helps keep the lights on.
          </p>
          <a
            href={process.env.REACT_APP_BMC_URL || 'https://buymeacoffee.com/ohapotato'}
            target="_blank"
            rel="noopener noreferrer"
            style={s.coffeeLink}
          >
            ☕ Buy me a coffee
          </a>
        </section>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/contact" style={s.footerLink}>Contact</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
        </footer>
      </main>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', transition: 'background 0.3s, color 0.3s' },
  nav:  { padding: '16px 32px', borderBottom: '1px solid var(--border-base)' },
  navHome: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--accent-orange)', textDecoration: 'none', fontWeight: '600' },
  main: { maxWidth: '720px', margin: '0 auto', padding: '48px 24px 80px' },

  hero: { textAlign: 'center', marginBottom: '56px' },
  heroEmoji: { fontSize: '52px', marginBottom: '16px' },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: '400', lineHeight: '1.2', marginBottom: '18px', color: 'var(--text-primary)' },
  heroSub: { fontFamily: 'var(--font-body)', fontSize: '16px', color: 'var(--text-secondary)', lineHeight: '1.7', maxWidth: '520px', margin: '0 auto' },

  section: { marginBottom: '48px' },
  sectionTitle: { fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '400', color: 'var(--text-primary)', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid var(--border-base)' },

  steps: { listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '16px' },
  step:  { display: 'flex', gap: '16px', alignItems: 'flex-start', fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.6' },
  stepNum: { flexShrink: 0, width: '28px', height: '28px', borderRadius: '50%', background: 'var(--accent-orange)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px', marginTop: '1px' },

  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  featureCard: { background: 'var(--bg-card)', border: '1px solid var(--border-base)', borderRadius: 'var(--radius-md)', padding: '20px' },
  featureIcon: { fontSize: '24px', display: 'block', marginBottom: '10px' },
  featureTitle: { fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px' },
  featureBody: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', margin: 0 },

  prose: { fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.75', marginBottom: '16px' },

  authorBox: { display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' },
  authorAvatar: { width: '48px', height: '48px', borderRadius: '50%', background: 'var(--accent-orange)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '14px', fontWeight: '700', flexShrink: 0 },
  authorName: { fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)', margin: '0 0 2px' },
  authorHandle: { fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-muted)', margin: 0 },

  footer: { marginTop: '64px', paddingTop: '24px', borderTop: '1px solid var(--border-base)', display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' },
  footerLink: { fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-muted)', textDecoration: 'none' },
  footerDot: { color: 'var(--text-muted)', fontSize: '11px' },
  coffeeLink: {
    display: 'inline-block', marginTop: '4px',
    padding: '10px 22px', borderRadius: 'var(--radius-pill)',
    background: '#FFDD00', color: '#000',
    fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '600',
    textDecoration: 'none', transition: 'opacity 0.15s ease',
  },
};
